import { useMemo, type ReactNode } from 'react'
// Subpath, NOT the '@edge/charts' barrel — this provider mounts eagerly in
// main.tsx and the barrel would pull the klinecharts runtime into the entry
// bundle (see the '@edge/charts/interaction' precedent).
import { ChartThemeColorsContext, themedColorsFrom } from '@edge/charts/theme-colors'
import { useSettings } from '@/context/settings-hooks'
import { buildChartTheme } from './chart-theme'
import { currentChartColorDefaults } from './chart-color-defaults'

/**
 * Publishes the active theme's chart colors so every `SettingsModal` can render
 * theme-accurate swatches.
 *
 * Without this the swatches fall back to the stored values, which for a key
 * that's following the theme is klinecharts' factory teal — the panel would
 * display a color that isn't anywhere on the chart.
 *
 * Rebuilt on `themeVersion` so a same-mode preset switch (Edge Dark → Glacier)
 * re-resolves the CSS vars; `buildChartTheme` reads them live from the DOM.
 */
export function ChartThemeColorsProvider({ children }: { children: ReactNode }) {
  const { themeVersion } = useSettings()
  const value = useMemo(
    () => {
      // Candle/grid/crosshair/separator defaults come out of the klinecharts
      // style bundle; the session-overlay defaults (pre-market / after-hours /
      // day break) don't live there — they're painted to <canvas>, so we publish
      // the active profile's concrete color strings alongside. A `null` break
      // (the light profile ships none) is omitted, which `useMarketSession` reads
      // as "draw no line unless the user picked one".
      const session = currentChartColorDefaults()
      return {
        ...themedColorsFrom(buildChartTheme()),
        preMarketColor: session.preMarket,
        afterHoursColor: session.afterHours,
        ...(session.sessionBreak ? { sessionBreakColor: session.sessionBreak } : {}),
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [themeVersion],
  )
  return (
    <ChartThemeColorsContext.Provider value={value}>{children}</ChartThemeColorsContext.Provider>
  )
}
